import { Component, ComponentInterface, h, Host, JSX, Prop } from '@stencil/core';
import { BaseCategory, UserDecision } from '@usercentrics/cmp-browser-sdk';
import ConsentHelper from '../../ConsentHelper/ConsentHelper';
import { Constants } from '../../constants';
import { BannerVariant } from '../../types/BannerVariant';
import { CookieSettingsLabels } from '../../types/CookieSettingsLabels';
import { Icons } from '../../types/Icons';
import { Direction } from '../../utils/getTextDirection';
import { linkConverter } from '../../utils/linkConverter';
import { stringCleaner } from '../../utils/stringCleaner';

@Component({
  tag: 'cmm-cookie-settings',
  styleUrl: 'cmm-cookie-settings.scss',
  shadow: false,
})
export class CookieSettings implements ComponentInterface {
  @Prop() categories: BaseCategory[] = null;
  @Prop() direction: Direction = Constants.LEFT_TO_RIGHT;
  @Prop() labels: CookieSettingsLabels = null;
  @Prop() showNotification: boolean;
  @Prop() userDecision: UserDecision[];
  @Prop() variant: BannerVariant;
  @Prop() handleCategoryChange: (event: MouseEvent) => void;
  @Prop() handlerAcceptAll: () => void;
  @Prop() handlerSaveSelection: () => void;
  @Prop() handlerToggleDetails: () => void;
  @Prop() handleSelectAllChange: (event: MouseEvent) => void;
  @Prop() handleServiceChange: (event: MouseEvent) => void;
  @Prop() toggleCategoryDetails: (category: BaseCategory) => void;
  @Prop() toggleConsentInfoModal: (id: string) => void;

  private getCategoryDescription(initialDescription: string): string[] {
    // 1. Clean description from UC formatter
    const cleanedDescription = stringCleaner(initialDescription);
    // 2. Split description by paragraph
    const splitDescription = cleanedDescription?.split('<br>');
    // 3. Convert anchor links in proper html format
    return linkConverter(splitDescription);
  }

  public render(): JSX.Element {
    return (
      <Host>
        <div id="cookie-settings">
          <div class="cookie-settings-content">
            <ul>
              <li class="select-all-item">
                <label class="select-all--label cmm-checkbox">
                  <input
                    type="checkbox"
                    checked={false}
                    class={`cmm-checkbox-input cmm-checkbox-input--category ${ConsentHelper.getCheckboxAllClass(
                      this.userDecision,
                    )}`}
                    data-test="handle-select-all-change"
                    onClick={(event: MouseEvent): void => this.handleSelectAllChange(event)}
                  />
                  <div class="cmm-checkbox-fake" />
                  {this.labels?.selectAll}
                </label>
              </li>
              {this.categories?.map((category: BaseCategory) => (
                <li class="category-item" data-category-wrapper={category.label} key={category.label}>
                  <div class="category-select-all">
                    <label class="category-label cmm-checkbox">
                      <input
                        type="checkbox"
                        checked={ConsentHelper.getCategoryChecked(category) || category.isEssential}
                        class={`cmm-checkbox-input cmm-checkbox-input--category ${ConsentHelper.getCheckboxClass(
                          category,
                        )}`}
                        disabled={category.isEssential}
                        data-category={category.label}
                        data-test="handle-category-change"
                        onClick={(event: MouseEvent): void => this.handleCategoryChange(event)}
                      />
                      <div class="cmm-checkbox-fake" />
                      {category.label}
                    </label>
                  </div>

                  {category.description && Array.isArray(this.getCategoryDescription(category.description))
                    ? this.getCategoryDescription(category.description).map((paragraph) => (
                        <wb7-text
                          class="category-description"
                          size="m"
                          innerHTML={paragraph}
                          key={`category-${category.slug}`}
                        ></wb7-text>
                      ))
                    : ''}

                  {this.variant !== BannerVariant.SettingsPage && (
                    <wb7-button
                      class="toggle-button"
                      size="s"
                      variant="tertiary"
                      data-test="toggle-category-details"
                      aria-label={this.labels?.showDetails}
                      onClick={(): void => this.toggleCategoryDetails(category)}
                    >
                      <cmm-icon name={Icons.ARROW_UP}></cmm-icon>
                    </wb7-button>
                  )}

                  <ul class="consent-list">
                    {category?.services?.map((service) => (
                      <li class="consent-item">
                        <label class="consent-label cmm-checkbox">
                          <input
                            type="checkbox"
                            checked={service.consent.status}
                            class="cmm-checkbox-input"
                            disabled={service.isEssential || category.isEssential}
                            onClick={(event: MouseEvent): void => this.handleServiceChange(event)}
                            data-test="handle-consent-change"
                            data-template-id={service.id}
                            data-category={category.label}
                          />
                          <div class="cmm-checkbox-fake" />
                          <div class="consent-item__information">
                            <span class="consent-item__name">{service.name}</span>
                            <button
                              type="button"
                              class="consent-item__icon"
                              data-test="toggle-consent-info-modal"
                              aria-label={service.name}
                              onClick={(): void => this.toggleConsentInfoModal(service.id)}
                            >
                              <cmm-icon name={Icons.INFO}></cmm-icon>
                            </button>
                          </div>
                        </label>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>

            {this.variant !== BannerVariant.SettingsPage && (
              <wb7-button
                class="toggle-button"
                data-test="toggle-cookie-details"
                variant="tertiary"
                size="s"
                onClick={(): void => this.handlerToggleDetails()}
              >
                <cmm-icon name={Icons.ARROW_UP} aria-hidden="true"></cmm-icon>
                {this.labels?.hideDetails}
              </wb7-button>
            )}

            <div class="button-group">
              {this.variant !== BannerVariant.Information && (
                <wb7-button
                  class="button button--confirm-selection"
                  data-test="handle-confirm-selection-button"
                  variant="primary"
                  size="s"
                  onClick={(): void => this.handlerSaveSelection()}
                >
                  {this.labels?.save}
                </wb7-button>
              )}
              <wb7-button
                class="button button--accept-all"
                data-test="handle-accept-all-button"
                variant="primary"
                size="s"
                onClick={(): void => this.handlerAcceptAll()}
              >
                {this.labels?.acceptAll}
              </wb7-button>
            </div>
            {this.showNotification && (
              <cmm-notification direction={this.direction} content={this.labels?.consentChanged}></cmm-notification>
            )}
          </div>
        </div>
      </Host>
    );
  }
}
